import { useEffect, useMemo, useRef, useState } from 'react';
import { llpl, hasBlocks } from './labEngine';
import './LLPLTest.css';

const n = (s) => (s === '' || s == null ? NaN : Number(s));

// "cont" = container number. Kept for traceability only; never used in the calculation.
const blankLL = () => ({ cont: '', blows: '', wetG: '', dryG: '', tareG: '' });
const blankPL = () => ({ cont: '', wetG: '', dryG: '', tareG: '' });

// A row counts only if every weight is a real, physically possible number.
const llComplete = (r) => {
  const blows = n(r.blows), w = n(r.wetG), d = n(r.dryG), t = n(r.tareG);
  return [blows, w, d, t].every(Number.isFinite) && blows > 0 && t > 0 && d > t && w >= d;
};
const plComplete = (r) => {
  const w = n(r.wetG), d = n(r.dryG), t = n(r.tareG);
  return [w, d, t].every(Number.isFinite) && t > 0 && d > t && w >= d;
};
const toLL = (r) => ({ blows: n(r.blows), wetG: n(r.wetG), dryG: n(r.dryG), tareG: n(r.tareG) });
const toPL = (r) => ({ wetG: n(r.wetG), dryG: n(r.dryG), tareG: n(r.tareG) });

const rowStarted = (r) => Object.entries(r).some(([k, v]) => k !== 'cont' && v !== '');
const rowHint = (r, ok) => {
  if (ok || !rowStarted(r)) return null;
  const w = n(r.wetG), d = n(r.dryG), t = n(r.tareG);
  if ([w, d, t].every(Number.isFinite)) {
    if (t <= 0) return 'Container weight must be above 0';
    if (d <= t) return 'Container + dry must be more than container';
    if (w < d) return 'Container + wet must be at least container + dry';
  }
  return 'Row incomplete — ignored in calculation';
};

// Two rows with identical weights are one test counted twice, not two determinations.
const findDuplicates = (rows, label, keys) => {
  const seen = new Map(), out = [];
  rows.forEach((r, i) => {
    const sig = keys.map((k) => r[k]).join('|');
    if (seen.has(sig)) {
      out.push({
        level: 'block', code: 'DUPLICATE_ROW', clause: 'data integrity',
        msg: `${label} ${seen.get(sig) + 1} and ${i + 1} have identical readings — enter the real second reading, do not copy a row`,
      });
    } else seen.set(sig, i);
  });
  return out;
};

const Cell = ({ value, onChange, step = '0.01', type = 'number', label, ph }) => (
  <input
    className="llpl-input"
    type={type} step={type === 'number' ? step : undefined} min={type === 'number' ? 0 : undefined}
    inputMode={type === 'number' ? 'decimal' : 'text'}
    placeholder={ph} aria-label={label} value={value}
    onChange={(e) => onChange(e.target.value)}
  />
);

const Labels = ({ cols }) => (
  <div className={`llpl-grid llpl-labels ${cols.length === 5 ? 'llpl-grid-5' : 'llpl-grid-4'}`}>
    {cols.map((c) => <span key={c}>{c}</span>)}
  </div>
);

// Liquid & plastic limit test. Project details (header) live in the portal; this form only holds the test data.
// It reports its state upward through onReport({ status, ready, dirty, summary, payload }).
export default function LLPLTest({ onReport }) {
  const [ll, setLL] = useState(() => Array.from({ length: 4 }, blankLL));
  const [pl, setPL] = useState(() => Array.from({ length: 3 }, blankPL));
  const [plNotObtainable, setNP] = useState(false);
  const [prep, setPrep] = useState({ soilHistory: '', soakHours: '' });

  const upd = (setter) => (i, k, v) => setter((rows) => rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const setLLc = upd(setLL), setPLc = upd(setPL);
  const removeRow = (setter, min) => (i) => setter((rows) => (rows.length > min ? rows.filter((_, j) => j !== i) : rows));

  const { result, issues } = useMemo(() => {
    const llRows = ll.filter(llComplete);
    const plRows = plNotObtainable ? [] : pl.filter(plComplete);
    if (!llRows.length) return { result: null, issues: [] };
    const out = llpl({
      ll: llRows.map(toLL), pl: plRows.map(toPL), plNotObtainable,
      soilHistory: prep.soilHistory, soakHours: n(prep.soakHours),
    });
    const dups = [
      ...findDuplicates(llRows, 'LL trial', ['wetG', 'dryG', 'tareG']),
      ...findDuplicates(plRows, 'PL determination', ['wetG', 'dryG', 'tareG']),
    ];
    return { result: out.result, issues: [...out.issues, ...dups] };
  }, [ll, pl, plNotObtainable, prep.soilHistory, prep.soakHours]);

  const resultOk = !!result && Number.isFinite(result.LL) && result.LL > 0 && (result.PL == null || Number.isFinite(result.PL));
  const ready = resultOk && !hasBlocks(issues);
  const dirty = ll.some(rowStarted) || pl.some(rowStarted) || plNotObtainable || prep.soilHistory !== '' || prep.soakHours !== '';

  const report = useMemo(() => ({
    status: ready ? 'ready' : dirty ? 'incomplete' : 'empty',
    ready, dirty,
    hint: ready ? '' : !dirty ? 'enter the readings' : (issues.find((i) => i.level === 'block')?.msg || 'complete the readings'),
    summary: ready ? `LL ${result.LL} · PL ${plNotObtainable ? 'NP' : result.PL ?? '—'} · PI ${plNotObtainable ? 'NP' : result.PI ?? '—'}` : '',
    payload: ready ? {
      inputs: {
        ll: ll.filter(llComplete).map((r) => ({ cont: r.cont, ...toLL(r) })),
        pl: plNotObtainable ? [] : pl.filter(plComplete).map((r) => ({ cont: r.cont, ...toPL(r) })),
        plNotObtainable, soilHistory: prep.soilHistory, soakHours: n(prep.soakHours),
      },
      result, issues,
    } : null,
  }), [ready, dirty, result, issues, ll, pl, plNotObtainable, prep]);

  const cb = useRef(onReport); cb.current = onReport;
  useEffect(() => { cb.current?.(report); }, [report]);

  return (
    <div className="llpl">
      <section className="llpl-block-sec">
        <div className="llpl-sec-head">
          <h4>Sample preparation</h4>
        </div>
        <div className="llpl-prep">
          <label className="llpl-field">Soil history
            <select className="llpl-select" value={prep.soilHistory} onChange={(e) => setPrep({ ...prep, soilHistory: e.target.value })}>
              <option value="">Select…</option>
              <option value="natural">Natural</option>
              <option value="air-dried">Air-dried</option>
              <option value="oven-dried">Oven-dried</option>
              <option value="unknown">Unknown</option>
            </select>
          </label>
          <label className="llpl-field">Soaking after mixing (h)
            <input className="llpl-input" type="number" min="0" value={prep.soakHours}
              onChange={(e) => setPrep({ ...prep, soakHours: e.target.value })} />
          </label>
        </div>
      </section>

      <section className="llpl-block-sec">
        <div className="llpl-sec-head">
          <h4>Liquid limit trials</h4>
          <span className="llpl-muted">minimum 4 trials, 15–35 blows</span>
        </div>
        <div className="llpl-scroll">
          <Labels cols={['Container no.', 'Blows', 'Container + wet (g)', 'Container + dry (g)', 'Container (g)']} />
          {ll.map((r, i) => {
            const hint = rowHint(r, llComplete(r));
            return (
              <div key={i} className="llpl-row">
                <div className="llpl-grid llpl-grid-5">
                  <Cell type="text" label="Container no." ph="No." value={r.cont} onChange={(v) => setLLc(i, 'cont', v)} />
                  <Cell step="1" label="Blows" value={r.blows} onChange={(v) => setLLc(i, 'blows', v)} />
                  <Cell label="Container + wet" value={r.wetG} onChange={(v) => setLLc(i, 'wetG', v)} />
                  <Cell label="Container + dry" value={r.dryG} onChange={(v) => setLLc(i, 'dryG', v)} />
                  <div className="llpl-last">
                    <Cell label="Container" value={r.tareG} onChange={(v) => setLLc(i, 'tareG', v)} />
                    {ll.length > 4 && (
                      <button type="button" className="llpl-x" aria-label={`Remove trial ${i + 1}`} onClick={() => removeRow(setLL, 4)(i)}>×</button>
                    )}
                  </div>
                </div>
                {hint && <p className="llpl-hint">Trial {i + 1}: {hint}</p>}
              </div>
            );
          })}
        </div>
        <button type="button" className="llpl-link" onClick={() => setLL((r) => [...r, blankLL()])}>+ Add trial</button>
      </section>

      <section className="llpl-block-sec">
        <div className="llpl-sec-head">
          <h4>Plastic limit determinations</h4>
          <label className="llpl-check">
            <input type="checkbox" checked={plNotObtainable} onChange={(e) => setNP(e.target.checked)} />
            Non-plastic (PL not obtainable)
          </label>
        </div>
        {!plNotObtainable && (
          <>
            <div className="llpl-scroll">
              <Labels cols={['Container no.', 'Container + wet (g)', 'Container + dry (g)', 'Container (g)']} />
              {pl.map((r, i) => {
                const hint = rowHint(r, plComplete(r));
                return (
                  <div key={i} className="llpl-row">
                    <div className="llpl-grid llpl-grid-4">
                      <Cell type="text" label="Container no." ph="No." value={r.cont} onChange={(v) => setPLc(i, 'cont', v)} />
                      <Cell label="Container + wet" value={r.wetG} onChange={(v) => setPLc(i, 'wetG', v)} />
                      <Cell label="Container + dry" value={r.dryG} onChange={(v) => setPLc(i, 'dryG', v)} />
                      <div className="llpl-last">
                        <Cell label="Container" value={r.tareG} onChange={(v) => setPLc(i, 'tareG', v)} />
                        {pl.length > 3 && (
                          <button type="button" className="llpl-x" aria-label={`Remove determination ${i + 1}`} onClick={() => removeRow(setPL, 3)(i)}>×</button>
                        )}
                      </div>
                    </div>
                    {hint && <p className="llpl-hint">Determination {i + 1}: {hint}</p>}
                  </div>
                );
              })}
            </div>
            <button type="button" className="llpl-link" onClick={() => setPL((r) => [...r, blankPL()])}>+ Add determination</button>
          </>
        )}
      </section>

      {resultOk && (
        <section className="llpl-result">
          <div className="llpl-stat"><span>Liquid limit</span><b>{result.LL}</b></div>
          <div className="llpl-stat"><span>Plastic limit</span><b>{plNotObtainable ? 'NP' : (result.PL ?? '—')}</b></div>
          <div className="llpl-stat"><span>Plasticity index</span><b>{plNotObtainable ? 'NP' : (result.PI ?? '—')}</b></div>
          <div className="llpl-stat llpl-stat-small"><span>Flow index / R²</span><b>{result.flowIndex.toFixed(1)} / {result.r2.toFixed(3)}</b></div>
        </section>
      )}

      {issues.length > 0 && (
        <ul className="llpl-issues">
          {issues.map((it, i) => (
            <li key={i} className={it.level === 'block' ? 'llpl-err' : 'llpl-warn'}>
              <b>{it.level === 'block' ? 'Fix' : 'Check'}</b> {it.msg} <span className="llpl-muted">({it.clause})</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}